import * as React from 'react';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { connect } from 'react-redux';
import SortableTree, {
  addNodeUnderParent,
  changeNodeAtPath,
  removeNodeAtPath,
  TreeItem,
} from 'react-sortable-tree';
import { toast } from 'react-toastify';
import { MODALS } from 'core/modals/config';
import 'react-sortable-tree/style.css';
import { IModalData, withModals } from 'core/modals/ModalsProvider';
import { Box } from '@material-ui/core';
import { RouteComponentProps } from 'react-router';
import Form from 'components/form/Form';
import { FormLoader } from 'components/form/FormLoader';
import { Error } from 'components/error/Error';
import { STATUSES } from 'core/api';
import { IReduxStoreState } from 'core/store/interfaces';
import { decorators, formConfig } from './config';
import {
  bulkUpdateAdminMenu,
  createAdminMenu,
  deleteAdminMenuItem,
  fetchAdminMenuItem,
  resetAdminMenuInfo,
  updateAdminMenu,
  fetchAdminMenu,
  fetchNestedAdminMenu,
} from './redux';

import styles from './Menu.module.styl';
import { IAdminMenuItem } from './types';

const INPUT_WIDTH = 400;
const MAX_INPUT_LENGTH = 300;

const initialValues = {
  title: '',
  slug: '',
  isPublished: false,
  isDefault: false,
  permission: {
    countries: null,
    departments: null,
    seniorities: null,
    teams: null,
    titles: null,
    workplaces: null,
  },
};

interface IProps {
  fetchAdminMenu: (id: string) => any;
  fetchAdminMenuItem: (id: string) => void;
  fetchNestedAdminMenu: (id: string) => void;
  menuItem: IReduxStoreState['adminMenu']['menuItem'];
  adminMenu: IReduxStoreState['adminMenu']['adminMenu'];
  openModal: (modal: string, data: IModalData) => void;
  resetAdminMenuInfo: () => void;
}

type Props = IProps & RouteComponentProps<{ id: string }>;

interface IState {
  isDisabled: boolean;
  isExpanded: boolean;
  treeData: TreeItem[];
}
// This component has migrated to async version. In order to add plus icon, we are adding dummy children
class Menu extends React.Component<Props, IState> {
  state = {
    isDisabled: true,
    isExpanded: false,
    treeData: [],
  };

  public componentDidMount(): void {
    const {
      match: {
        params: { id },
      },
      fetchAdminMenuItem,
      fetchAdminMenu,
    } = this.props;

    fetchAdminMenu('');
    if (id) {
      fetchAdminMenuItem(id);
    }
  }

  public componentDidUpdate = (prevProps: Props) => {
    const {
      match: {
        params: { id },
      },
      resetAdminMenuInfo,
      fetchAdminMenuItem,
      adminMenu,
      adminMenu: {
        data: { items },
      },
    } = this.props;

    if (
      adminMenu.status === STATUSES.SUCCESS &&
      adminMenu.data !== prevProps.adminMenu.data
    ) {
      const newItems = addDummyChildren(items);
      this.setState({
        treeData: newItems,
      });
    }

    if (id === prevProps.match.params.id) {
      return;
    }

    if (!id) {
      resetAdminMenuInfo();
    } else {
      fetchAdminMenuItem(id);
    }
  };

  public componentWillUnmount() {
    const { resetAdminMenuInfo } = this.props;
    resetAdminMenuInfo();
  }

  private readonly generateNodeProps = (props: any) => {
    const { node, path } = props;
    const { history } = this.props;

    const getNodeKey = ({ treeIndex }: any) => treeIndex;

    const handleAdd = () =>
      this.setState((state: IState) => ({
        isDisabled: true,
        treeData: addNodeUnderParent({
          expandParent: true,
          getNodeKey,
          newNode: {
            isPublished: true,
            title: '',
          },
          parentKey: path[path.length - 1],
          treeData: state.treeData,
        }).treeData,
      }));

    const removeNodeFromTree = () =>
      this.setState(state => ({
        treeData: removeNodeAtPath({
          getNodeKey,
          path,
          treeData: state.treeData,
        }),
      }));

    const handleDelete = () => {
      const { openModal } = this.props;
      openModal(MODALS.CONFIRM, {
        caption: 'Warning',
        onConfirm: () => {
          this.sendDeleteNode(node, removeNodeFromTree);
        },
        text: 'Are you sure you want to delete this menu item?',
      });
      return;
    };

    const handleEdit = () => {
      history.push(`/admin/menu/${node.id}`);
    };

    const handleTitleChange = (event: any) => {
      event.preventDefault();
      console.log('@debug', event);
      const title = event.target.value;

      if (title.length > 0) {
        this.setState(state => ({
          ...state,
          isDisabled: false,
        }));
      } else {
        this.setState(state => ({
          ...state,
          isDisabled: true,
        }));
      }
      this.setState(state => ({
        treeData: changeNodeAtPath({
          getNodeKey,
          newNode: { ...node, title },
          path,
          treeData: state.treeData,
        }),
      }));
    };

    if (node.title === 'dummy children') return { style: { display: 'none' } };

    return {
      onMouseEnter: async () => {
        await this.fetchAsyncChildren(node);
      },

      buttons: [
        ...(node?.title?.length > 0
          ? [
              <IconButton key="add" aria-label="A" onClick={handleAdd}>
                <AddIcon />
              </IconButton>,
            ]
          : []),

        <IconButton key="delete" aria-label="A" onClick={handleDelete}>
          <DeleteIcon />
        </IconButton>,
        <IconButton
          key="edit"
          aria-label="A"
          onClick={handleEdit}
          disabled={!node.id}
        >
          <EditIcon />
        </IconButton>,
      ],
      title: (
        <Box>
          <IconButton key="add2" aria-label="A" disabled={true}>
            {node.isPublished ? (
              <Visibility className={styles.eye} />
            ) : (
              <VisibilityOff className={styles.eye} />
            )}
          </IconButton>

          <input
            style={{ fontSize: '1.1rem', width: INPUT_WIDTH }}
            className="form-control"
            maxLength={MAX_INPUT_LENGTH}
            value={node.title}
            onChange={handleTitleChange}
          />
        </Box>
      ),
    };
  };

  private readonly handleChange = (newTreeData: any) => {
    const { treeData: oldTreeData } = this.state;

    if (
      JSON.stringify(newTreeData) === JSON.parse(JSON.stringify(oldTreeData))
    ) {
      return;
    }
    this.setState({ treeData: newTreeData });
  };

  private readonly fetchAsyncChildren = async (
    node: any,
    draggedNode?: any,
  ) => {
    const { fetchNestedAdminMenu } = this.props;
    // if the node is empty or does not have children or we have moved the child out of the parent
    if (node?.title === '' || !node?.children || node?.children.length === 0)
      return;
    // if the node fetched the children then we dont need to fetch the children again
    if (
      node?.children.length > 0 &&
      !node?.children.find((child: any) => child.title === 'dummy children')
    )
      return;
    const res: any = await fetchNestedAdminMenu(node.id);
    // if we fetch children during a dnd operation we need to add dragged node to the children
    const newChildren = draggedNode
      ? [...res?.items, ...[draggedNode]]
      : res?.items;
    const newTree = addChildrenToTree(this.state.treeData, newChildren);
    const newWithChildren = addDummyChildren(newTree);
    this.setState({ treeData: newWithChildren });
  };

  private readonly renderMenuTree = () => {
    const { treeData } = this.state;
    return (
      <SortableTree
        style={{ height: 'calc(100% - 50px)' }}
        treeData={treeData}
        onChange={this.handleChange}
        generateNodeProps={this.generateNodeProps}
        getNodeKey={({ treeIndex }) => treeIndex}
        canDrop={({ nextParent }) => nextParent?.title !== 'dummy children'}
        onMoveNode={({ node, nextParentNode }) => {
          this.fetchAsyncChildren(nextParentNode, node);
        }}
      />
    );
  };

  private readonly sendChange = async () => {
    const { treeData } = this.state;
    const { fetchAdminMenu } = this.props;
    const result = removeDummyChildren(treeData);
    try {
      await bulkUpdateAdminMenu(result);
      await fetchAdminMenu('');
      this.setState({
        isDisabled: true,
      });
      toast.success('Success!');
    } catch (e) {
      toast.error('Error!');
    }
  };

  private readonly sendDeleteNode = async (
    node: any,
    removeNodeFromTree: () => void,
  ) => {
    const { match, history } = this.props;

    try {
      await deleteAdminMenuItem(node.id);
      toast.success('Success!');
      removeNodeFromTree();
    } catch (error) {
      const { message } = error as Error;
      toast.error(message);
    }
    if (match.params.id === node.id) {
      history.push('/admin/menu/');
    }
  };

  private readonly validate = (values: any) => {
    const errors: Record<string, string> = {};

    const requiredFields = formConfig.fields.filter(
      (field: any) => field.isRequired,
    );

    requiredFields.forEach((field: any) => {
      if (!values[field.name]) {
        errors[field.name] = 'Required Field';
      }
    });

    return errors;
  };

  private readonly onSubmit = async (values: any, onSuccess: any) => {
    const {
      history,
      match: {
        params: { id },
      },
      resetAdminMenuInfo,
      fetchAdminMenu,
    } = this.props;

    const valuesToSubmit = {
      ...values,
    };

    try {
      if (!id) {
        await createAdminMenu(valuesToSubmit);
      } else {
        await updateAdminMenu(id, valuesToSubmit);
      }
    } catch (e) {
      const { message } = e as Error;

      toast.error(message);
      return;
    }
    resetAdminMenuInfo();
    onSuccess();
    history.push('/admin/menu');
    toast.success('Success!');
    fetchAdminMenu('');
    return true;
  };

  private readonly renderForm = () => {
    const {
      menuItem,
      match: {
        params: { id },
      },
      adminMenu,
    } = this.props;

    switch (menuItem.status) {
      case STATUSES.REQUEST:
        return <FormLoader config={formConfig} />;
      case STATUSES.FAILED:
        return <Error>{menuItem.error}</Error>;
      case STATUSES.SUCCESS:
        return (
          <Form
            key="update"
            config={formConfig}
            onSubmit={this.onSubmit}
            validate={this.validate}
            initialValues={menuItem.data.data || initialValues}
            menu={
              adminMenu.status === STATUSES.SUCCESS ? adminMenu.data.items : []
            }
            withTranslation="menu"
            decorators={decorators}
          />
        );
      case STATUSES.INITIAL:
        return id ? (
          <FormLoader config={formConfig} />
        ) : (
          <Form
            key="create"
            config={formConfig}
            onSubmit={this.onSubmit}
            validate={this.validate}
            initialValues={initialValues}
            menu={
              adminMenu.status === STATUSES.SUCCESS ? adminMenu.data.items : []
            }
            withTranslation="menu"
            decorators={decorators}
          />
        );
      default:
        return null;
    }
  };

  public render() {
    const { adminMenu } = this.props;

    return (
      <div className={styles.root}>
        {this.renderForm()}
        <div>
          <div className={styles.menuTreeHeader}>
            <Typography variant="h4" id="tableTitle">
              Menu
            </Typography>
            <Box>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={this.sendChange}
                disabled={this.state.isDisabled}
              >
                Apply changes
              </Button>
            </Box>
          </div>
          {adminMenu.status === STATUSES.SUCCESS && this.renderMenuTree()}
        </div>
      </div>
    );
  }
}

const mapState = (state: IReduxStoreState) => ({
  adminMenu: state.adminMenu.adminMenu,
  menuItem: state.adminMenu.menuItem,
});

const mapDispatch = {
  fetchAdminMenuItem,
  resetAdminMenuInfo,
  fetchAdminMenu,
  fetchNestedAdminMenu,
};

export default connect(mapState, mapDispatch)(withModals(Menu));

export const addChildrenToTree = (tree: any[], children: any[]) => {
  tree.forEach(item => {
    if (item?.id === (children && children[0]?.parentId)) {
      // if item is a parent use its link or if it is a child add its parent's link
      item.children = children.map(child => {
        return child;
      });
    }
    if (item?.hasChildren && item?.children) {
      addChildrenToTree(item.children, children);
    }
  });
  return tree;
};
// we need to add dummy children to show plus icon in the library
const addDummyChildren = (items: IAdminMenuItem[]) => {
  items.forEach(item => {
    if (item.hasChildren) {
      if (!item.children) {
        item.children = [{ title: 'dummy children' }];
      }
      addDummyChildren(item.children);
    }
    return item;
  });
  return items;
};

// we need to remove dummy children before submit
const removeDummyChildren = (items: IAdminMenuItem[]) => {
  const result = items.map(item => {
    if (item.hasChildren) {
      if (item.children) {
        item.children = item.children.filter(
          child => child.title !== 'dummy children',
        );
      }
      removeDummyChildren(item.children);
    }
    return item;
  });
  return result;
};
